import { useState } from "react";
import { api } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { useNation } from "../lib/useNation";
import { Page, Card, Spinner, ErrorBox } from "../components/ui";
import { PollWidget } from "../components/PollWidget";
import { VerifyAccount } from "../components/VerifyAccount";
import { ApiError } from "../lib/apiError";
import { POLL_DURATION_PRESET_DAYS, POLL_LEVEL_MAX, POLL_LEVEL_MIN, POLL_MAX_DURATION_MS, POLL_MAX_OPTIONS, POLL_MIN_OPTIONS } from "@shared/poll";
import type { GenericPoll } from "@shared/types";
import { editToken } from "../lib/editToken";
import { useT } from "../i18n";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Resolve the form's closing-time choice to an epoch-ms value + validity.
 * "none" is only valid for a verified creator; "custom" must be a future time
 * within the max horizon; a preset is N days out. */
function resolveEndChoice(choice: string, customEnd: string, nowMs: number): { ok: boolean; value: number | null } {
  // "No end date" is always submittable; the server decides whether the caller
  // is verified enough to leave a poll open, and the form prompts verification
  // (then retries) if not.
  if (choice === "none") return { ok: true, value: null };
  if (choice === "custom") {
    const ms = customEnd ? new Date(customEnd).getTime() : NaN;
    const ok = Number.isFinite(ms) && ms > nowMs && ms <= nowMs + POLL_MAX_DURATION_MS;
    return { ok, value: ok ? ms : null };
  }
  return { ok: true, value: nowMs + Number(choice) * DAY_MS };
}

export default function Polls() {
  const t = useT();
  const { nation } = useNation();
  const [reloadKey, setReloadKey] = useState(0);
  const { data, loading, error } = useAsync(() => api.polls(), [reloadKey]);
  // Country names/flags for the per-poll scope tag (esp. the "other nations" list).
  const countries = useAsync(() => api.countries(), []);
  const countryById = new Map((countries.data ?? []).map((c) => [c.id, c]));
  const scopeFor = (countryId: string | null): { name: string; code: string } | "global" =>
    countryId ? countryById.get(countryId) ?? { name: "Unknown", code: "" } : "global";

  const all = data ?? [];
  const mine = nation ? all.filter((p) => p.countryId === nation.id) : [];
  const global = all.filter((p) => p.countryId === null);
  const others = all.filter((p) => p.countryId !== null && (!nation || p.countryId !== nation.id));

  return (
    <Page>
      <h1 className="text-xl font-semibold my-3">🗳 {t("polls.title")}</h1>
      <p className="text-muted text-sm mb-4">{t("polls.blurb")}</p>

      <CreatePoll onCreated={() => setReloadKey((k) => k + 1)} />

      {loading && <Spinner label={t("polls.loading")} />}
      {error && <ErrorBox msg={error} />}

      {nation && <PollGroup title={t("polls.nationCommunity", { nation: nation.name })} polls={mine} empty={t("polls.emptyNation")} />}
      <PollGroup title={`🌍 ${t("polls.global")}`} polls={global} empty={t("polls.emptyGlobal")} />
      {others.length > 0 && <PollGroup title={t("polls.otherNations")} polls={others} scopeFor={scopeFor} />}
    </Page>
  );
}

function PollGroup({
  title,
  polls,
  empty,
  scopeFor,
}: {
  title: string;
  polls: GenericPoll[];
  empty?: string;
  scopeFor?: (countryId: string | null) => { name: string; code: string } | "global";
}) {
  return (
    <section className="mt-6">
      <h2 className="text-xs uppercase tracking-wide text-muted font-semibold mb-2">{title}</h2>
      {polls.length === 0 ? (
        empty ? <p className="text-muted text-sm">{empty}</p> : null
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {polls.map((p) => (
            <PollWidget key={p.id} poll={p} scope={scopeFor ? scopeFor(p.countryId) : undefined} linkToDetail />
          ))}
        </div>
      )}
    </section>
  );
}

function CreatePoll({ onCreated }: { onCreated: () => void }) {
  const t = useT();
  const { nation } = useNation();
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState<string[]>(["", ""]);
  const [attachNation, setAttachNation] = useState(true);
  const [requireVerified, setRequireVerified] = useState(false);
  const [minLevel, setMinLevel] = useState(""); // empty = no minimum
  // Only a verified creator (holds a per-user token) can leave a poll open and
  // close it on demand; everyone else must pick a closing time up front.
  const isVerified = editToken.getUser() != null;
  const [endChoice, setEndChoice] = useState<string>(isVerified ? "none" : String(POLL_DURATION_PRESET_DAYS[2]));
  const [customEnd, setCustomEnd] = useState(""); // datetime-local value when endChoice === "custom"
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Set when the server rejects an open-ended poll because the caller isn't
  // verified — we surface the verify flow inline and retry once it succeeds.
  const [needsVerify, setNeedsVerify] = useState(false);

  const filled = options.map((o) => o.trim()).filter(Boolean);
  // A min level needs an identity, so it always implies verified (mirrors the
  // server rule) — reflect that in the UI by forcing the toggle on.
  const levelSet = minLevel.trim() !== "";
  const levelNum = levelSet ? Number(minLevel) : null;
  const levelValid = !levelSet || (Number.isInteger(levelNum) && levelNum! >= POLL_LEVEL_MIN && levelNum! <= POLL_LEVEL_MAX);
  const end = resolveEndChoice(endChoice, customEnd, Date.now());
  const canSubmit = question.trim().length > 0 && filled.length >= POLL_MIN_OPTIONS && levelValid && end.ok;

  function setOption(i: number, v: string) {
    setOptions((prev) => prev.map((o, idx) => (idx === i ? v : o)));
  }

  async function submit() {
    setBusy(true);
    setErr(null);
    setNeedsVerify(false);
    try {
      await api.createPoll({
        question: question.trim(),
        options: filled,
        countryId: attachNation && nation ? nation.id : null,
        requireVerified: requireVerified || levelSet,
        minLevel: levelNum,
        endDate: end.value,
      });
      setQuestion("");
      setOptions(["", ""]);
      setRequireVerified(false);
      setMinLevel("");
      setEndChoice("none");
      setCustomEnd("");
      setOpen(false);
      onCreated();
    } catch (e) {
      // An open-ended poll by an unverified creator: prompt verification inline
      // (VerifyAccount stores the token), then retrying the submit succeeds.
      if (e instanceof ApiError && e.code === "end_date_required") {
        setNeedsVerify(true);
      } else {
        setErr((e as Error).message);
      }
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="bg-accent text-white rounded-lg px-4 py-2 text-sm font-semibold">
        + {t("polls.create")}
      </button>
    );
  }

  return (
    <Card>
      <h2 className="font-semibold mb-3">{t("polls.create")}</h2>
      <label className="block text-sm mb-1 text-muted">{t("polls.question")}</label>
      <input
        className="w-full bg-panel2 border border-border rounded px-2 py-1.5 text-sm mb-3"
        value={question}
        maxLength={200}
        onChange={(e) => setQuestion(e.target.value)}
        placeholder={t("polls.questionPlaceholder")}
      />
      <label className="block text-sm mb-1 text-muted">{t("polls.options")}</label>
      <div className="space-y-2 mb-2">
        {options.map((o, i) => (
          <div key={i} className="flex gap-2">
            <input
              className="flex-1 bg-panel2 border border-border rounded px-2 py-1.5 text-sm"
              value={o}
              maxLength={80}
              onChange={(e) => setOption(i, e.target.value)}
              placeholder={t("polls.optionN", { n: i + 1 })}
            />
            {options.length > POLL_MIN_OPTIONS && (
              <button onClick={() => setOptions((prev) => prev.filter((_, idx) => idx !== i))} className="text-muted text-sm px-2" title={t("common.remove")}>
                ✕
              </button>
            )}
          </div>
        ))}
      </div>
      {options.length < POLL_MAX_OPTIONS && (
        <button onClick={() => setOptions((prev) => [...prev, ""])} className="text-accent text-sm mb-3">
          + {t("polls.addOption")}
        </button>
      )}

      <div className="flex items-center gap-2 mt-2 mb-1 text-sm">
        <span className="text-muted">{t("polls.attachTo")}</span>
        {nation && (
          <button
            onClick={() => setAttachNation(true)}
            className={`px-2.5 py-1 rounded-full border text-xs ${attachNation ? "border-accent text-accent" : "border-border text-muted"}`}
          >
            {nation.name}
          </button>
        )}
        <button
          onClick={() => setAttachNation(false)}
          className={`px-2.5 py-1 rounded-full border text-xs ${!attachNation || !nation ? "border-accent text-accent" : "border-border text-muted"}`}
        >
          🌍 {t("polls.global")}
        </button>
      </div>

      <div className="mt-3 pt-3 border-t border-border space-y-2">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={requireVerified || levelSet}
            disabled={levelSet}
            onChange={(e) => setRequireVerified(e.target.checked)}
          />
          <span>{t("polls.requireVerified")}</span>
        </label>
        <p className="text-muted text-xs">{t("polls.requireVerifiedHint")}</p>

        <label className="block text-sm text-muted">{t("polls.minLevel")}</label>
        <input
          type="number"
          min={POLL_LEVEL_MIN}
          max={POLL_LEVEL_MAX}
          value={minLevel}
          onChange={(e) => setMinLevel(e.target.value)}
          placeholder={t("polls.minLevelPlaceholder")}
          className={`w-28 bg-panel2 border rounded px-2 py-1.5 text-sm ${levelValid ? "border-border" : "border-red-500"}`}
        />
        <p className="text-muted text-xs">{t("polls.minLevelHint")}</p>
      </div>

      <div className="mt-3 pt-3 border-t border-border space-y-2">
        <label className="block text-sm text-muted">{t("polls.endDate")}</label>
        <div className="flex flex-wrap gap-2">
          {POLL_DURATION_PRESET_DAYS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setEndChoice(String(d))}
              className={`px-2.5 py-1 rounded-full border text-xs ${endChoice === String(d) ? "border-accent text-accent" : "border-border text-muted"}`}
            >
              {t("polls.endDays", { n: d })}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setEndChoice("custom")}
            className={`px-2.5 py-1 rounded-full border text-xs ${endChoice === "custom" ? "border-accent text-accent" : "border-border text-muted"}`}
          >
            {t("polls.endCustom")}
          </button>
          <button
            type="button"
            onClick={() => setEndChoice("none")}
            className={`px-2.5 py-1 rounded-full border text-xs ${endChoice === "none" ? "border-accent text-accent" : "border-border text-muted"}`}
          >
            {t("polls.endNone")}
          </button>
        </div>
        {endChoice === "custom" && (
          <input
            type="datetime-local"
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
            className={`bg-panel2 border rounded px-2 py-1.5 text-sm ${end.ok ? "border-border" : "border-red-500"}`}
          />
        )}
        <p className="text-muted text-xs">{isVerified ? t("polls.endHint") : t("polls.endRequiredHint")}</p>
      </div>

      {needsVerify && (
        <div className="mt-3">
          <p className="text-sm text-cyan font-semibold">{t("polls.endVerifyPrompt")}</p>
          <VerifyAccount onVerified={() => void submit()} />
        </div>
      )}
      {err && <div className="mt-3"><ErrorBox msg={err} /></div>}
      <div className="flex gap-2 mt-3">
        <button
          disabled={!canSubmit || busy}
          onClick={submit}
          className="bg-accent text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
        >
          {busy ? t("ama.posting") : t("polls.post")}
        </button>
        <button onClick={() => setOpen(false)} className="text-muted text-sm px-3">{t("common.cancel")}</button>
      </div>
    </Card>
  );
}
